/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { ServiceEndpoint } from 'client-code/sls-service-endpoints';
import { DefaultServices } from '../infrastructure/DefaultServices';
import { RetryMechanism } from '../retrymechanism/RetryMechanism';
import { DEFAULT_RETRY_OPTIONS, TIVO_PRE_SIGN_IN_SERVICES } from '../../config';
import { RetryOptions } from '../retrymechanism/RetryOptions';
import { BrandingUiInfoKeys } from '../entities/BrandingUiInfoKeys';
import { RecoveryMechanism } from '../recoverymechanism/RecoveryMechanism';
import { IDefaultServices } from '../interfaces/IDefaultServices';
import { EmptyResponseError } from '../error/EmptyResponseError';
import { ErrorTypes } from '../error/ErrorTypes';
import { PartialResponseError } from '../error/PartialResponseError';
import { Locale } from '../locale/Locale';
import { LocaleKeys } from '../locale/LocaleKeys';
import { DeviceModel } from '../device/DeviceModel';
import { Branding } from '../branding/Branding';
import { DimensionScaler } from '../device/DimensionScaler';

export class DefaultServicesClientImpl {
    private defaultServices: IDefaultServices;

    constructor() {
        this.defaultServices = new DefaultServices(DeviceModel.getSlsBaseUrl());
    }

    private isSlsGlobalResIncomplete = (slsEndpoints) => {
        const services = slsEndpoints.map((endpoint) => {
            return endpoint.service;
        });
        const preSignInServices = Object.values(TIVO_PRE_SIGN_IN_SERVICES);
        for (let i = 0; i < preSignInServices.length; i += 1) {
            if (!services.includes(preSignInServices[i])) {
                return true;
            }
        }
        return false;
    };

    private formatErrorDescription = (description: string) => {
        let formattedDescription = description;
        formattedDescription = formattedDescription.replace(
            'PHONE_CUSTOMER_SUPPORT_NUMBER',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER),
        );
        formattedDescription = formattedDescription.replace(
            'CUSTOMER_SUPPORT_NAME',
            this.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
        );
        return formattedDescription;
    };

    private validateSlsResponse = (slsGlobalEndpoints: ServiceEndpoint[]) => {
        if (!slsGlobalEndpoints.length) {
            throw new EmptyResponseError(
                ErrorTypes.SLS_EMPTY_RESPONSE_C156,
                'Received empty response from SlsGlobalEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
        if (this.isSlsGlobalResIncomplete(slsGlobalEndpoints)) {
            throw new PartialResponseError(
                ErrorTypes.SLS_PARTIAL_RESPONSE_C155,
                'Received partial response from SlsGlobalEndpointGet',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    fetchMsoDefaultBrandingUiInfo = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const fetchMsoDefaultBrandingUi =
                this.defaultServices.fetchMsoDefaultBrandingUiInfo.bind(this.defaultServices);
            RetryMechanism.callServiceWithRetry<void>(
                fetchMsoDefaultBrandingUi,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => resolve())
                .catch((error) => reject(error));
        });
    };

    fetchSlsGlobalEndpoints = (): Promise<ServiceEndpoint[]> => {
        return new Promise((resolve, reject) => {
            const fetchGlobalEndpoints = this.defaultServices.fetchGlobalEndPoints.bind(
                this.defaultServices,
            );

            RetryMechanism.callServiceWithRetry<ServiceEndpoint[]>(
                fetchGlobalEndpoints,
                [DeviceModel.getSlsBaseUrl()],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((slsEndpoints: ServiceEndpoint[]) => {
                    this.validateSlsResponse(slsEndpoints);
                    resolve(slsEndpoints);
                })
                .catch((error) => reject(error));
        });
    };

    waitForSlsGlobalEndpoints = (): Promise<ServiceEndpoint[]> => {
        return new Promise((resolve) => {
            const fetchGlobalEndpoints = this.defaultServices.fetchGlobalEndPoints.bind(
                this.defaultServices,
            );

            RecoveryMechanism.callServiceWithRecovery<ServiceEndpoint[]>(fetchGlobalEndpoints, [
                DeviceModel.getSlsBaseUrl(),
            ]).then((slsEndpoints: ServiceEndpoint[]) => {
                if (!slsEndpoints.length || this.isSlsGlobalResIncomplete(slsEndpoints))
                    resolve(this.waitForSlsGlobalEndpoints());
                else resolve(slsEndpoints);
            });
        });
    };

    getBrandingUiInfoForKey = (key: string): any => {
        try {
            return this.defaultServices.getBrandingUiInfoForKey(key);
        } catch (error) {
            return Branding.getFallbackBrandingUiInfoForKey(key);
        }
    };

    /** This function will return the nearest image in terms of width and area.. */

    getPartnerBrandingImageUrl(width: number, height: number): string {
        const { height: scaledHeight, width: scaledWidth } =
            DimensionScaler.getInstance().getScaledDimensions(width, height);
        const images = this.getBrandingUiInfoForKey(BrandingUiInfoKeys.IMAGE)?.images || [];
        let bestImage = images[0];
        const targetArea = scaledWidth * scaledHeight;
        const currentImageArea = bestImage.width * bestImage.height;
        let deltaArea = Math.abs(targetArea - currentImageArea);
        let deltaWidth = Math.abs(bestImage.width - scaledWidth);

        for (let i = 1; i < images.length; i += 1) {
            const image = images[i];
            const newDeltaWidth = Math.abs(image.width - scaledWidth);
            const newDeltaImageArea = Math.abs(image.width * image.height - targetArea);

            if (newDeltaWidth === 0 && newDeltaImageArea === 0) {
                bestImage = image;
                break;
            }
            if (newDeltaWidth <= deltaWidth && newDeltaImageArea <= deltaArea) {
                deltaWidth = newDeltaWidth;
                deltaArea = newDeltaImageArea;
                bestImage = image;
            }
        }
        return bestImage.imageUrl;
    }
}
